import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Badge, Modal, Form, InputGroup, Spinner } from 'react-bootstrap';
import { 
  FaLayerGroup, FaPlus, FaTrash, FaEdit, FaSearch, FaMapMarkerAlt, 
  FaBoxes, FaExclamationCircle, FaTimes, FaWarehouse, 
  FaThLarge, FaArrowsAlt, FaInfoCircle, FaPills, FaEye, 
  FaRedo, FaChevronLeft
} from 'react-icons/fa';
import { get, post, put, del } from '../utils/api';
import { useToast } from './ToastContext';
import './RackManagement.css';

const RackManagement = () => {
  const { showToast } = useToast();

  // State: Racks list and active selection
  const [racks, setRacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRackId, setSelectedRackId] = useState(null);
  const [selectedRackDetails, setSelectedRackDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Filter & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [zoneFilter, setZoneFilter] = useState('ALL');

  // Medicine Finder
  const [finderQuery, setFinderQuery] = useState('');
  const [finderResults, setFinderResults] = useState([]);
  const [searchingFinder, setSearchingFinder] = useState(false);
  const [highlightedSlotId, setHighlightedSlotId] = useState(null);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditRackModal, setShowEditRackModal] = useState(false);
  const [showDeleteRackModal, setShowDeleteRackModal] = useState(false);
  const [showAddShelfModal, setShowAddShelfModal] = useState(false);
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // Delete Slot & Shelf Confirmations
  const [showDeleteSlotModal, setShowDeleteSlotModal] = useState(false);
  const [slotToDelete, setSlotToDelete] = useState(null);
  const [deletingSlot, setDeletingSlot] = useState(false);

  const [showDeleteShelfModal, setShowDeleteShelfModal] = useState(false);
  const [shelfToDelete, setShelfToDelete] = useState(null);
  const [deletingShelf, setDeletingShelf] = useState(false);

  // Form states for Create Rack
  const [newRackForm, setNewRackForm] = useState({
    rack_name: '',
    zone: 'Zone A',
    row_count: 6,
    col_count: 6,
    description: '',
    auto_generate: true,
  });

  // Form state for Edit Rack
  const [editRackForm, setEditRackForm] = useState({
    rack_name: '',
    zone: '',
    description: '',
  });

  // Form state for Add Shelf
  const [newShelfForm, setNewShelfForm] = useState({
    shelf_label: '',
    slot_count: 6,
  });

  // Medicine Assignment State
  const [allMedicines, setAllMedicines] = useState([]);
  const [assignedMedicineIds, setAssignedMedicineIds] = useState(new Set());
  const [loadingMedicines, setLoadingMedicines] = useState(false);
  const [selectedMedicineId, setSelectedMedicineId] = useState('');
  const [assignmentNotes, setAssignmentNotes] = useState('');
  const [assigningMed, setAssigningMed] = useState(false);

  // 1. Fetch Racks List
  const fetchRacks = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (zoneFilter && zoneFilter !== 'ALL') queryParams.append('zone', zoneFilter);
      if (searchQuery.trim()) queryParams.append('search', searchQuery.trim());

      const res = await get(`/racks?${queryParams.toString()}`);
      if (res && res.success) {
        setRacks(res.data || []);
      } else {
        showToast(res?.message || 'Failed to load racks', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Error fetching racks', 'error');
    } finally {
      setLoading(false);
    }
  }, [zoneFilter, searchQuery, showToast]);

  // Fetch assigned medicine IDs across all racks
  const fetchAssignedMedicines = useCallback(async () => {
    try {
      const res = await get('/racks/assigned-medicines');
      if (res && res.success && Array.isArray(res.assignedMedicineIds)) {
        setAssignedMedicineIds(new Set(res.assignedMedicineIds.map(Number)));
      }
    } catch (err) {
      console.error('Failed to load assigned medicines:', err);
    }
  }, []);

  useEffect(() => {
    fetchRacks();
    fetchAssignedMedicines();
  }, [fetchRacks, fetchAssignedMedicines]);

  // 2. Fetch Selected Rack 2D Grid Details
  const fetchRackDetails = useCallback(async (rackId) => {
    if (!rackId) return;
    setLoadingDetails(true);
    try {
      const res = await get(`/racks/${rackId}`);
      if (res && res.success) {
        setSelectedRackDetails(res.data);
      } else {
        showToast(res?.message || 'Failed to load rack layout', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Error fetching rack details', 'error');
    } finally {
      setLoadingDetails(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (selectedRackId) {
      fetchRackDetails(selectedRackId);
    } else {
      setSelectedRackDetails(null);
    }
  }, [selectedRackId, fetchRackDetails]);

  // 3. Fetch Medicines for mapping dropdown
  const fetchMedicinesList = useCallback(async (force = false) => {
    if (!force && allMedicines.length > 0) return;
    setLoadingMedicines(true);
    try {
      const res = await get('/medicines?limit=500');
      if (res && res.success && Array.isArray(res.data)) {
        setAllMedicines(res.data);
      }
    } catch (err) {
      console.error('Failed to load medicines for rack mapping:', err);
    } finally {
      setLoadingMedicines(false);
    }
  }, [allMedicines.length]);

  // 4. Quick Medicine Finder
  const handleFindMedicine = async (e) => {
    if (e) e.preventDefault();
    if (!finderQuery.trim()) {
      setFinderResults([]);
      return;
    }
    setSearchingFinder(true);
    try {
      const res = await get(`/racks/search?q=${encodeURIComponent(finderQuery.trim())}`);
      if (res && res.success) {
        setFinderResults(res.data || []);
        if (res.data.length === 0) {
          showToast(`No rack location found for "${finderQuery}"`, 'info');
        }
      }
    } catch (err) {
      showToast('Search failed: ' + err.message, 'error');
    } finally {
      setSearchingFinder(false);
    }
  };

  const jumpToLocation = (loc) => {
    setSelectedRackId(loc.rack_id);
    setHighlightedSlotId(loc.slot_id);
    setTimeout(() => {
      const element = document.getElementById(`rack-slot-${loc.slot_id}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 500);
  };

  // 5. Handle Create Rack
  const handleCreateRack = async (e) => {
    e.preventDefault();
    if (!newRackForm.rack_name.trim()) {
      showToast('Please enter a rack name', 'warning');
      return;
    }
    try {
      const res = await post('/racks', newRackForm);
      if (res && res.success) {
        showToast(res.message || 'Rack created successfully!', 'success');
        setShowCreateModal(false);
        setNewRackForm({
          rack_name: '',
          zone: 'Zone A',
          row_count: 6,
          col_count: 6,
          description: '',
          auto_generate: true,
        });
        fetchRacks();
        if (res.data?.rack_id) {
          setSelectedRackId(res.data.rack_id);
        }
      } else {
        showToast(res?.message || 'Failed to create rack', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Error creating rack', 'error');
    }
  };

  // 6. Handle Edit Rack
  const handleEditRack = async (e) => {
    e.preventDefault();
    if (!selectedRackDetails) return;
    try {
      const res = await put(`/racks/${selectedRackDetails.rack_id}`, editRackForm);
      if (res && res.success) {
        showToast('Rack updated successfully', 'success');
        setShowEditRackModal(false);
        fetchRacks();
        fetchRackDetails(selectedRackDetails.rack_id);
      } else {
        showToast(res?.message || 'Failed to update rack', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Error updating rack', 'error');
    }
  };

  // 7. Handle Delete Rack
  const handleDeleteRack = async () => {
    if (!selectedRackDetails) return;
    try {
      const res = await del(`/racks/${selectedRackDetails.rack_id}`);
      if (res && res.success) {
        showToast('Rack deleted successfully', 'success');
        setShowDeleteRackModal(false);
        setSelectedRackId(null);
        setSelectedRackDetails(null);
        fetchRacks();
      } else {
        showToast(res?.message || 'Failed to delete rack', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Error deleting rack', 'error');
    }
  };

  // 8. Handle Add Shelf
  const handleAddShelf = async (e) => {
    e.preventDefault();
    if (!selectedRackId) return;
    try {
      const res = await post(`/racks/${selectedRackId}/shelves`, newShelfForm);
      if (res && res.success) {
        showToast(res.message || 'Shelf added successfully', 'success');
        setShowAddShelfModal(false);
        setNewShelfForm({ shelf_label: '', slot_count: 6 });
        fetchRackDetails(selectedRackId);
        fetchRacks();
      } else {
        showToast(res?.message || 'Failed to add shelf', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Error adding shelf', 'error');
    }
  };

  // 9. Handle Delete Shelf (with dedicated styled modal)
  const confirmDeleteShelf = (shelf, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setShelfToDelete(shelf);
    setShowDeleteShelfModal(true);
  };

  const executeDeleteShelf = async () => {
    if (!shelfToDelete) return;
    setDeletingShelf(true);
    try {
      const res = await del(`/racks/shelves/${shelfToDelete.shelf_id}`);
      if (res && res.success) {
        showToast('Shelf deleted successfully', 'success');
        setShowDeleteShelfModal(false);
        setShelfToDelete(null);
        await fetchRackDetails(selectedRackId);
        await fetchRacks();
      } else {
        showToast(res?.message || 'Failed to delete shelf', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Error deleting shelf', 'error');
    } finally {
      setDeletingShelf(false);
    }
  };

  // 10. Handle Add Slot to Shelf
  const handleAddSlotToShelf = async (shelfId) => {
    try {
      const res = await post(`/racks/shelves/${shelfId}/slots`, {});
      if (res && res.success) {
        showToast('Slot added to shelf', 'success');
        fetchRackDetails(selectedRackId);
        fetchRacks();
      } else {
        showToast(res?.message || 'Failed to add slot', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Error adding slot', 'error');
    }
  };

  // 11. Handle Delete Slot (with dedicated styled modal)
  const confirmDeleteSlot = (slot, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setSlotToDelete(slot);
    setShowDeleteSlotModal(true);
  };

  const executeDeleteSlot = async () => {
    if (!slotToDelete) return;
    setDeletingSlot(true);
    try {
      const res = await del(`/racks/slots/${slotToDelete.slot_id}`);
      if (res && res.success) {
        showToast(`Slot ${slotToDelete.slot_code || ''} deleted successfully`, 'success');
        setShowDeleteSlotModal(false);
        setShowSlotModal(false);
        setSlotToDelete(null);
        await fetchRackDetails(selectedRackId);
        await fetchRacks();
      } else {
        showToast(res?.message || 'Failed to delete slot', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Error deleting slot', 'error');
    } finally {
      setDeletingSlot(false);
    }
  };

  // 12. Open Slot Modal (Medicine Assignment)
  const openSlotModal = (slot, shelf) => {
    setSelectedSlot({ ...slot, shelf_label: shelf.shelf_label, shelf_number: shelf.shelf_number });
    fetchMedicinesList(true);
    fetchAssignedMedicines();
    setSelectedMedicineId('');
    setAssignmentNotes('');
    setShowSlotModal(true);
  };

  // 13. Assign Medicine to Slot
  const handleAssignMedicine = async (e) => {
    e.preventDefault();
    if (!selectedSlot || !selectedMedicineId) {
      showToast('Please choose a medicine to assign', 'warning');
      return;
    }
    setAssigningMed(true);
    try {
      const res = await post(`/racks/slots/${selectedSlot.slot_id}/medicines`, {
        medicine_id: selectedMedicineId,
        notes: assignmentNotes.trim(),
      });
      if (res && res.success) {
        showToast(res.message || 'Medicine assigned to slot successfully', 'success');
        const assignedId = Number(selectedMedicineId);
        setAssignedMedicineIds(prev => new Set([...prev, assignedId]));
        setSelectedMedicineId('');
        setAssignmentNotes('');
        await fetchRackDetails(selectedRackId);
        await fetchRacks();
        await fetchAssignedMedicines();
        await fetchMedicinesList(true);
        const targetShelf = selectedRackDetails?.shelves?.find(s => s.shelf_number === selectedSlot.shelf_number);
        const updatedSlot = targetShelf?.slots?.find(sl => sl.slot_id === selectedSlot.slot_id);
        if (updatedSlot) {
          setSelectedSlot(prev => ({
            ...prev,
            medicines: updatedSlot.medicines
          }));
        }
      } else {
        showToast(res?.message || 'Failed to assign medicine', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Error assigning medicine', 'error');
    } finally {
      setAssigningMed(false);
    }
  };

  // 14. Remove Medicine from Slot
  const handleRemoveMedicine = async (medicineId) => {
    if (!selectedSlot) return;
    try {
      const res = await del(`/racks/slots/${selectedSlot.slot_id}/medicines/${medicineId}`);
      if (res && res.success) {
        showToast('Medicine removed from slot', 'success');
        const removedId = Number(medicineId);
        setAssignedMedicineIds(prev => {
          const next = new Set(prev);
          next.delete(removedId);
          return next;
        });
        await fetchRackDetails(selectedRackId);
        await fetchRacks();
        await fetchAssignedMedicines();
        await fetchMedicinesList(true);
        setSelectedSlot(prev => ({
          ...prev,
          medicines: (prev.medicines || []).filter(m => m.medicine_id !== medicineId)
        }));
      } else {
        showToast(res?.message || 'Failed to remove medicine', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Error removing medicine', 'error');
    }
  };

  // Computed Zones list
  const availableZones = useMemo(() => {
    const zones = new Set(['Zone A', 'Zone B', 'Dispensary Front', 'Cold Storage']);
    racks.forEach(r => {
      if (r.zone) zones.add(r.zone);
    });
    return Array.from(zones);
  }, [racks]);

  // Available medicines not yet assigned anywhere in the rack system
  const unassignedMedicinesForSlot = useMemo(() => {
    if (!allMedicines || allMedicines.length === 0) return [];

    return allMedicines.filter(m => {
      const medId = Number(m.medicine_id);

      // 1. Check globally assigned IDs set from /racks/assigned-medicines
      if (assignedMedicineIds.has(medId)) return false;

      // 2. Check if m.rack_locations array returned from /medicines has any assigned location
      if (Array.isArray(m.rack_locations) && m.rack_locations.length > 0) return false;

      // 3. Check if currently in this selected slot
      if (selectedSlot?.medicines?.some(sm => Number(sm.medicine_id) === medId)) return false;

      // 4. Check if currently present anywhere in the selected rack layout
      if (selectedRackDetails?.shelves) {
        for (const shelf of selectedRackDetails.shelves) {
          if (shelf.slots) {
            for (const sl of shelf.slots) {
              if (sl.medicines?.some(sm => Number(sm.medicine_id) === medId)) {
                return false;
              }
            }
          }
        }
      }

      return true;
    });
  }, [allMedicines, assignedMedicineIds, selectedSlot, selectedRackDetails]);

  return (
    <Container fluid className="rack-management-container p-4">
      {/* Header Bar */}
      <div className="rack-header-card mb-4 p-4 shadow-sm rounded-4">
        <Row className="align-items-center g-3">
          <Col md={7}>
            <div className="d-flex align-items-center gap-3">
              <div className="rack-icon-badge">
                <FaWarehouse size={28} />
              </div>
              <div>
                <h2 className="fw-bold mb-1 rack-main-title">Dynamic Rack & Shelf Management</h2>
                <p className="text-muted mb-0 rack-subtitle">
                  Configure custom physical racks, variable row shelves, slot compartments, and manage medicine placements.
                </p>
              </div>
            </div>
          </Col>
          <Col md={5} className="text-md-end">
            <div className="d-flex gap-2 justify-content-md-end flex-wrap">
              <Button 
                variant="primary" 
                className="btn-create-rack shadow-sm px-3 py-2 fw-semibold d-flex align-items-center gap-2"
                onClick={() => setShowCreateModal(true)}
              >
                <FaPlus /> New Physical Rack
              </Button>
              {selectedRackId && (
                <Button 
                  variant="outline-secondary" 
                  className="px-3 py-2 fw-medium d-flex align-items-center gap-2"
                  onClick={() => setSelectedRackId(null)}
                >
                  <FaChevronLeft /> Back to All Racks
                </Button>
              )}
            </div>
          </Col>
        </Row>

        {/* Global Medicine Quick Finder Bar */}
        <div className="mt-4 pt-3 border-top rack-finder-section">
          <Form onSubmit={handleFindMedicine}>
            <InputGroup className="finder-input-group shadow-sm">
              <InputGroup.Text className="bg-white border-end-0">
                <FaSearch className="text-primary" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Quick Medicine Finder: Enter medicine name or generic to locate shelf & slot..."
                value={finderQuery}
                onChange={(e) => setFinderQuery(e.target.value)}
                className="border-start-0 ps-0 rack-search-input"
              />
              <Button 
                type="submit" 
                variant="primary" 
                className="px-4 fw-semibold"
                disabled={searchingFinder}
              >
                {searchingFinder ? <Spinner size="sm" animation="border" /> : 'Find Location'}
              </Button>
              {finderResults.length > 0 && (
                <Button 
                  variant="outline-secondary" 
                  onClick={() => { setFinderResults([]); setFinderQuery(''); }}
                >
                  <FaTimes />
                </Button>
              )}
            </InputGroup>
          </Form>
          <div className="rack-finder-tip-text small mt-2 d-flex align-items-center flex-wrap">
            <span className="badge rack-tip-badge border me-2">💡 Tip</span>
            <span>Type any medicine name to find its physical rack, shelf, and compartment immediately.</span>
          </div>

          {/* Finder Results Drawer */}
          {finderResults.length > 0 && (
            <div className="finder-results-card mt-3 p-3 rounded-3 shadow-sm">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-bold text-dark">
                  Found {finderResults.length} Location{finderResults.length > 1 ? 's' : ''} for "{finderQuery}"
                </span>
              </div>
              <div className="d-flex flex-wrap gap-2">
                {finderResults.map((loc, idx) => (
                  <div key={idx} className="finder-result-chip p-2 rounded-3 border d-flex align-items-center gap-3">
                    <div>
                      <div className="fw-bold text-primary">{loc.medicine_name}</div>
                      <div className="text-muted small">
                        <strong>{loc.rack_name}</strong> ({loc.zone || 'Main'}) ? Shelf {loc.shelf_number} ? Slot <strong>{loc.slot_code}</strong>
                      </div>
                      {loc.notes && <div className="text-info extra-small italic">Note: {loc.notes}</div>}
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline-primary"
                      className="btn-jump-location ms-auto"
                      onClick={() => jumpToLocation(loc)}
                    >
                      <FaEye /> Show on Rack
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* VIEW 1: All Racks List & Overview Cards */}
      {!selectedRackId && (
        <div className="racks-list-view">
          {/* Filter Bar */}
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <span className="fw-semibold text-muted me-1">Zone Filter:</span>
              <Button 
                variant={zoneFilter === 'ALL' ? 'primary' : 'outline-secondary'} className={`rounded-pill px-3 rack-zone-btn ${zoneFilter === 'ALL' ? 'active' : ''}`} 
                size="sm" 
                className="rounded-pill px-3"
                onClick={() => setZoneFilter('ALL')}
              >
                All Zones
              </Button>
              {availableZones.map(zone => (
                <Button 
                  key={zone}
                  variant={zoneFilter === zone ? 'primary' : 'outline-secondary'} className={`rounded-pill px-3 rack-zone-btn ${zoneFilter === zone ? 'active' : ''}`} 
                  size="sm" 
                  className="rounded-pill px-3"
                  onClick={() => setZoneFilter(zone)}
                >
                  {zone}
                </Button>
              ))}
            </div>

            <div style={{ maxWidth: '280px', width: '100%' }}>
              <InputGroup size="sm" className="rack-search-input-group">
                <InputGroup.Text><FaSearch /></InputGroup.Text>
                <Form.Control
                  placeholder="Filter racks by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Loading racks and physical layout...</p>
            </div>
          ) : racks.length === 0 ? (
            <Card className="text-center py-5 border-dashed rounded-4">
              <Card.Body>
                <FaLayerGroup size={48} className="text-muted mb-3" />
                <h4 className="fw-bold text-dark">No Physical Racks Found</h4>
                <p className="text-muted max-w-md mx-auto">
                  Organize your pharmacy shelves by creating your first dynamic medicine rack.
                </p>
                <Button variant="primary" onClick={() => setShowCreateModal(true)} className="mt-2">
                  <FaPlus className="me-2" /> Create First Rack
                </Button>
              </Card.Body>
            </Card>
          ) : (
            <Row className="g-4">
              {racks.map(rack => {
                const totalSlots = rack.total_slots || 0;
                const occupiedSlots = rack.occupied_slots_count ?? rack.occupied_slots ?? 0;
                const occupancyRate = totalSlots > 0 ? Math.round((occupiedSlots / totalSlots) * 100) : 0;

                return (
                  <Col key={rack.rack_id} xl={4} lg={6} md={6}>
                    <Card className="rack-card h-100 shadow-sm border-0 rounded-4 overflow-hidden">
                      <div className="rack-card-header p-3 d-flex justify-content-between align-items-center">
                        <div>
                          <Badge bg="primary" className="px-2 py-1 mb-1 me-2">{rack.zone || 'Main Zone'}</Badge>
                          <h5 className="fw-bold mb-0 rack-name-text">{rack.rack_name}</h5>
                        </div>
                        <div className="rack-dimension-pill">
                          <FaThLarge className="me-1 text-muted" /> {rack.total_shelves || rack.row_count} Shelves
                        </div>
                      </div>

                      <Card.Body className="p-3">
                        <p className="text-muted small mb-3 rack-desc">
                          {rack.description || 'Standard pharmacy storage unit for medicine inventory.'}
                        </p>

                        {/* Visual Shelf Preview Mini Bars */}
                        <div className="mini-rack-preview mb-3 p-2 rounded-3 bg-light border">
                          <div className="d-flex justify-content-between small text-muted mb-1">
                            <span>Storage Capacity</span>
                            <span className="fw-bold text-dark">{occupiedSlots} / {totalSlots} Slots ({occupancyRate}%)</span>
                          </div>
                          <div className="progress rack-progress" style={{ height: '8px' }}>
                            <div 
                              className={`progress-bar ${occupancyRate > 80 ? 'bg-warning' : 'bg-success'}`} 
                              style={{ width: `${occupancyRate}%` }}
                            />
                          </div>
                          <div className="mini-shelves-stack mt-2 d-flex flex-column gap-1">
                            {Array.from({ length: Math.min(rack.total_shelves || 4, 5) }).map((_, idx) => (
                              <div key={idx} className="mini-shelf-line" />
                            ))}
                          </div>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                          <span className="small text-muted">
                            <FaPills className="text-primary me-1" /> <strong>{rack.total_medicines_stored ?? rack.stored_medicines_count ?? 0}</strong> Meds Placed
                          </span>
                          <Button 
                            variant="primary" 
                            size="sm" 
                            className="px-3 rounded-pill fw-semibold d-flex align-items-center gap-2"
                            onClick={() => setSelectedRackId(rack.rack_id)}
                          >
                            Open 2D Studio <FaArrowsAlt />
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </div>
      )}

      {/* VIEW 2: Interactive 2D Rack Studio (When a Rack is Selected) */}
      {selectedRackId && (
        <div className="rack-studio-view">
          {loadingDetails ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Loading 2D rack blueprint and compartment inventory...</p>
            </div>
          ) : selectedRackDetails ? (
            <div>
              {/* Studio Action & Info Toolbar */}
              <div className="studio-toolbar p-3 mb-4 rounded-4 shadow-sm bg-white d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div className="d-flex align-items-center gap-3">
                  <Button 
                    variant="light" 
                    size="sm" 
                    className="border rounded-circle p-2"
                    onClick={() => setSelectedRackId(null)}
                    title="Back to All Racks"
                  >
                    <FaChevronLeft />
                  </Button>
                  <div>
                    <div className="d-flex align-items-center gap-2">
                      <h4 className="fw-bold mb-0 studio-rack-title">{selectedRackDetails.rack_name}</h4>
                      <Badge bg="info" className="px-2 py-1">{selectedRackDetails.zone || 'Zone A'}</Badge>
                    </div>
                    <span className="text-muted small">
                      {selectedRackDetails.shelves?.length || 0} Shelves ? {
                        (selectedRackDetails.shelves || []).reduce((acc, s) => acc + (s.slots?.length || 0), 0)
                      } Compartment Slots
                    </span>
                  </div>
                </div>

                <div className="d-flex gap-2 align-items-center flex-wrap">
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="fw-medium d-flex align-items-center gap-2"
                    onClick={() => setShowAddShelfModal(true)}
                  >
                    <FaPlus /> Add Shelf Row
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    className="fw-medium d-flex align-items-center gap-2"
                    onClick={() => {
                      setEditRackForm({
                        rack_name: selectedRackDetails.rack_name,
                        zone: selectedRackDetails.zone || '',
                        description: selectedRackDetails.description || '',
                      });
                      setShowEditRackModal(true);
                    }}
                  >
                    <FaEdit /> Edit Rack
                  </Button>
                  <Button 
                    variant="outline-danger" 
                    size="sm" 
                    className="fw-medium d-flex align-items-center gap-2"
                    onClick={() => setShowDeleteRackModal(true)}
                  >
                    <FaTrash /> Delete Rack
                  </Button>
                  <Button 
                    variant="light" 
                    size="sm" 
                    className="border"
                    onClick={() => fetchRackDetails(selectedRackId)}
                    title="Reload Blueprint"
                  >
                    <FaRedo />
                  </Button>
                </div>
              </div>

              {/* Physical Medical Rack Container (High Aesthetic Realistic Look) */}
              <div className="physical-rack-wrapper shadow-lg">
                <div className="physical-rack-top-header">
                  <div className="rack-metal-cap-left" />
                  <div className="rack-top-banner">
                    <span className="fw-bold text-uppercase tracking-wider">
                      {selectedRackDetails.rack_name} ? {selectedRackDetails.zone || 'General Storage'}
                    </span>
                  </div>
                  <div className="rack-metal-cap-right" />
                </div>

                <div className="physical-rack-frame">
                  {/* Left Upright Steel Pillar */}
                  <div className="rack-pillar-left">
                    <div className="pillar-slots-pattern" />
                  </div>

                  {/* Main Shelves Body */}
                  <div className="rack-shelves-stack">
                    {(!selectedRackDetails.shelves || selectedRackDetails.shelves.length === 0) ? (
                      <div className="text-center py-5 text-muted">
                        <p>This rack has no shelves yet.</p>
                        <Button variant="primary" size="sm" onClick={() => setShowAddShelfModal(true)}>
                          <FaPlus className="me-1" /> Add First Shelf
                        </Button>
                      </div>
                    ) : (
                      selectedRackDetails.shelves.map((shelf) => (
                        <div key={shelf.shelf_id} className="physical-shelf-unit">
                          {/* Shelf Header Strip */}
                          <div className="shelf-metal-header d-flex justify-content-between align-items-center px-3 py-1">
                            <div className="d-flex align-items-center gap-2">
                              <span className="shelf-badge-num">Shelf {shelf.shelf_number}</span>
                              <span className="shelf-custom-label fw-semibold">
                                {shelf.shelf_label || `Shelf Row ${shelf.shelf_number}`}
                              </span>
                              <span className="text-muted small">
                                ({shelf.slots?.length || 0} Slots)
                              </span>
                            </div>

                            <div className="d-flex align-items-center gap-2">
                              <Button 
                                size="sm" 
                                variant="light" 
                                className="shelf-action-btn d-flex align-items-center gap-1"
                                onClick={() => handleAddSlotToShelf(shelf.shelf_id)}
                                title="Add Compartment Slot to this Shelf"
                              >
                                <FaPlus size={10} /> Add Slot
                              </Button>
                              <Button 
                                size="sm" 
                                variant="light" 
                                className="shelf-action-btn text-danger d-flex align-items-center gap-1"
                                onClick={(e) => confirmDeleteShelf(shelf, e)}
                                title="Delete this entire Shelf"
                              >
                                <FaTrash size={10} /> Delete Shelf
                              </Button>
                            </div>
                          </div>

                          {/* Shelf Slots / Compartments Horizontal Grid */}
                          <div className="shelf-slots-row">
                            {(!shelf.slots || shelf.slots.length === 0) ? (
                              <div className="empty-shelf-prompt w-100 text-center py-2 text-muted small">
                                Empty shelf ? <span className="text-primary cursor-pointer fw-semibold" onClick={() => handleAddSlotToShelf(shelf.shelf_id)}>+ Add first compartment slot</span>
                              </div>
                            ) : (
                              shelf.slots.map((slot) => {
                                const isOccupied = slot.medicines && slot.medicines.length > 0;
                                const isHighlighted = highlightedSlotId === slot.slot_id;

                                return (
                                  <div 
                                    key={slot.slot_id}
                                    id={`rack-slot-${slot.slot_id}`}
                                    className={`slot-compartment ${isOccupied ? 'slot-occupied' : 'slot-empty'} ${isHighlighted ? 'slot-highlighted' : ''}`}
                                    onClick={() => openSlotModal(slot, shelf)}
                                  >
                                    <div className="slot-top-row d-flex justify-content-between align-items-center">
                                      <span className="slot-code-badge">{slot.slot_code}</span>
                                      <button 
                                        type="button" 
                                        className="btn-slot-delete-icon"
                                        onClick={(e) => confirmDeleteSlot(slot, e)}
                                        title={`Delete compartment slot ${slot.slot_code}`}
                                      >
                                        <FaTrash size={10} />
                                      </button>
                                    </div>

                                    {/* Medicines Inside Compartment */}
                                    <div className="slot-medicines-list">
                                      {isOccupied ? (
                                        slot.medicines.map((med) => (
                                          <div key={med.medicine_id} className="slot-medicine-chip" title={`${med.medicine_name} (${med.unit_name || 'piece'})`}>
                                            <span className="med-chip-dot" />
                                            <span className="med-chip-name">{med.medicine_name}</span>
                                          </div>
                                        ))
                                      ) : (
                                        <div className="slot-empty-placeholder">
                                          <FaPlus className="text-muted mb-1" />
                                          <span className="small text-muted">Empty Slot</span>
                                        </div>
                                      )}
                                    </div>

                                    {/* Slot Footer Indicator */}
                                    <div className="slot-footer-indicator">
                                      {isOccupied ? (
                                        <span className="text-success small fw-semibold">
                                          {slot.medicines.length} Med{slot.medicines.length > 1 ? 's' : ''}
                                        </span>
                                      ) : (
                                        <span className="text-muted extra-small">Click to assign</span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>

                          {/* Realistic Sturdy Metal Beam Under Shelf */}
                          <div className="shelf-under-beam" />
                        </div>
                      ))
                    )}
                  </div>

                  {/* Right Upright Steel Pillar */}
                  <div className="rack-pillar-right">
                    <div className="pillar-slots-pattern" />
                  </div>
                </div>

                {/* Bottom Base Beam */}
                <div className="physical-rack-base">
                  <div className="rack-leg-left" />
                  <div className="rack-base-strip" />
                  <div className="rack-leg-right" />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* MODAL 1: Create New Physical Rack */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered size="lg">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold d-flex align-items-center gap-2">
            <FaWarehouse className="text-primary" /> Create New Physical Rack
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateRack}>
          <Modal.Body className="pt-3">
            <Row className="g-3">
              <Col md={7}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Rack Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    required
                    placeholder="e.g., Rack A - Main Wall, Syrup Rack 1"
                    value={newRackForm.rack_name}
                    onChange={(e) => setNewRackForm({ ...newRackForm, rack_name: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Physical Zone / Location</Form.Label>
                  <Form.Select
                    value={newRackForm.zone}
                    onChange={(e) => setNewRackForm({ ...newRackForm, zone: e.target.value })}
                  >
                    <option value="Zone A">Zone A</option>
                    <option value="Zone B">Zone B</option>
                    <option value="Zone C">Zone C</option>
                    <option value="Front Dispensary">Front Dispensary</option>
                    <option value="Back Storage">Back Storage</option>
                    <option value="Cold Storage">Cold Storage</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Number of Shelves (Rows)</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    max={20}
                    value={newRackForm.row_count}
                    onChange={(e) => setNewRackForm({ ...newRackForm, row_count: parseInt(e.target.value) || 1 })}
                  />
                  <Form.Text className="text-muted">Vertical levels (default: 6, custom variable shelves allowed)</Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Default Slots per Shelf (Columns)</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    max={25}
                    value={newRackForm.col_count}
                    onChange={(e) => setNewRackForm({ ...newRackForm, col_count: parseInt(e.target.value) || 1 })}
                  />
                  <Form.Text className="text-muted">Divisions per shelf (e.g. 6 to 10 slots per shelf)</Form.Text>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Check
                  type="checkbox"
                  id="auto-generate-check"
                  label="Automatically generate shelves and slot compartments"
                  checked={newRackForm.auto_generate}
                  onChange={(e) => setNewRackForm({ ...newRackForm, auto_generate: e.target.checked })}
                  className="fw-semibold text-primary mb-3"
                />
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Description / Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Optional notes, e.g., Near billing counter, holds antibiotics & analgesics..."
                    value={newRackForm.description}
                    onChange={(e) => setNewRackForm({ ...newRackForm, description: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="light" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" className="px-4 fw-semibold">Create Rack</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODAL 2: Edit Rack Details */}
      <Modal show={showEditRackModal} onHide={() => setShowEditRackModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">Edit Rack Details</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditRack}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Rack Name</Form.Label>
              <Form.Control
                type="text"
                required
                value={editRackForm.rack_name}
                onChange={(e) => setEditRackForm({ ...editRackForm, rack_name: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Zone</Form.Label>
              <Form.Control
                type="text"
                value={editRackForm.zone}
                onChange={(e) => setEditRackForm({ ...editRackForm, zone: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editRackForm.description}
                onChange={(e) => setEditRackForm({ ...editRackForm, description: e.target.value })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setShowEditRackModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit">Save Changes</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODAL 3: Delete Rack Confirmation */}
      <Modal show={showDeleteRackModal} onHide={() => setShowDeleteRackModal(false)} centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="text-danger fw-bold d-flex align-items-center gap-2">
            <FaExclamationCircle /> Delete Physical Rack
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete <strong>{selectedRackDetails?.rack_name}</strong>?</p>
          <div className="modal-warning-callout">
            <FaExclamationCircle className="text-warning flex-shrink-0 mt-1" />
            <div>
              This will permanently remove this rack, all its shelves, slots, and unassign any medicine location references.
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="light" onClick={() => setShowDeleteRackModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleDeleteRack} className="fw-semibold">Delete Permanently</Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL 4: Add Shelf to Selected Rack */}
      <Modal show={showAddShelfModal} onHide={() => setShowAddShelfModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">Add Shelf Row to Rack</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddShelf}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Shelf Custom Label</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Top Shelf - Syrups & Liquids"
                value={newShelfForm.shelf_label}
                onChange={(e) => setNewShelfForm({ ...newShelfForm, shelf_label: e.target.value })}
              />
              <Form.Text className="text-muted">
                Give this shelf level a descriptive category or label (e.g., Top Shelf - Syrups & Liquids)
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Number of Compartment Slots</Form.Label>
              <Form.Control
                type="number"
                min={1}
                max={30}
                value={newShelfForm.slot_count}
                onChange={(e) => setNewShelfForm({ ...newShelfForm, slot_count: parseInt(e.target.value) || 1 })}
              />
              <Form.Text className="text-muted">How many slots to divide this shelf into (variable per shelf)</Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setShowAddShelfModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit">Add Shelf</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODAL 5: Compartment Slot Details & Medicine Placement */}
      <Modal show={showSlotModal} onHide={() => setShowSlotModal(false)} centered size="lg">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold d-flex align-items-center gap-2">
            <FaBoxes className="text-primary" /> Compartment: {selectedSlot?.slot_code}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          {selectedSlot && (
            <div>
              {/* Location Breadcrumb */}
              <div className="slot-location-pill p-2 px-3 rounded-3 border mb-3 d-flex align-items-center gap-2">
                <FaMapMarkerAlt className="text-primary" />
                <span className="fw-semibold">{selectedRackDetails?.rack_name}</span>
                <span className="text-muted">?</span>
                <span>Shelf {selectedSlot.shelf_number} ({selectedSlot.shelf_label || 'Standard'})</span>
                <span className="text-muted">?</span>
                <span className="fw-bold text-primary">Slot {selectedSlot.slot_code}</span>
              </div>

              {/* Medicines currently in this slot */}
              <h6 className="fw-bold slot-section-title mb-2">
                Medicines in this Compartment ({selectedSlot.medicines?.length || 0})
              </h6>
              {(!selectedSlot.medicines || selectedSlot.medicines.length === 0) ? (
                <div className="text-center py-4 empty-compartment-box rounded-3 text-muted mb-4 border-dashed">
                  <FaPills size={24} className="mb-2 text-muted" />
                  <div>No medicines currently assigned to this compartment.</div>
                </div>
              ) : (
                <div className="assigned-medicines-list mb-4">
                  {selectedSlot.medicines.map((med) => (
                    <div key={med.medicine_id} className="assigned-med-item p-3 mb-2 rounded-3 border d-flex justify-content-between align-items-center">
                      <div>
                        <div className="fw-bold assigned-med-name">{med.medicine_name}</div>
                        <div className="text-muted small">
                          {med.generic_name ? <span>Generic: <em>{med.generic_name}</em> ? </span> : null}
                          Base Unit: <Badge bg="light" text="dark" className="border">{med.unit_name || 'piece'}</Badge>
                        </div>
                        {med.notes && (
                          <div className="text-info extra-small mt-1">
                            <FaInfoCircle className="me-1" /> {med.notes}
                          </div>
                        )}
                      </div>
                      <Button 
                        variant="outline-danger" 
                        size="sm" 
                        className="btn-unassign-med"
                        onClick={() => handleRemoveMedicine(med.medicine_id)}
                      >
                        <FaTimes className="me-1" /> Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add / Assign Medicine Form */}
              <div className="assign-form-card p-3 rounded-3 border">
                <h6 className="fw-bold slot-form-title mb-3">
                  <FaPlus className="text-primary me-2" /> Place Medicine into this Slot
                </h6>
                <Form onSubmit={handleAssignMedicine}>
                  <Row className="g-2">
                    <Col md={7}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small fw-semibold d-flex justify-content-between align-items-center">
                          <span>Select Medicine from Inventory</span>
                          <Badge 
                            bg={unassignedMedicinesForSlot.length > 0 ? "light" : "warning"} 
                            text="dark" 
                            className="border"
                          >
                            {unassignedMedicinesForSlot.length} unassigned
                          </Badge>
                        </Form.Label>
                        <Form.Select
                          value={selectedMedicineId}
                          onChange={(e) => setSelectedMedicineId(e.target.value)}
                          disabled={loadingMedicines || unassignedMedicinesForSlot.length === 0}
                        >
                          <option value="">
                            {loadingMedicines 
                              ? '-- Loading Inventory Medicines... --' 
                              : unassignedMedicinesForSlot.length === 0 
                                ? '-- All Inventory Medicines Are Already Assigned --' 
                                : `-- Choose Medicine (${unassignedMedicinesForSlot.length} available) --`}
                          </option>
                          {unassignedMedicinesForSlot.map((med) => (
                            <option key={med.medicine_id} value={med.medicine_id}>
                              {med.name} {med.generic_name ? `(${med.generic_name})` : ''} - {med.unit_name || 'piece'}
                            </option>
                          ))}
                        </Form.Select>
                        {unassignedMedicinesForSlot.length === 0 && !loadingMedicines && (
                          <div className="text-muted extra-small mt-1 d-flex align-items-center gap-1">
                            <FaInfoCircle className="text-warning" />
                            <span>All medicines in inventory are already allocated to rack compartments.</span>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={5}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small fw-semibold">Placement Notes (Optional)</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. Left divider, 2nd stack"
                          value={assignmentNotes}
                          onChange={(e) => setAssignmentNotes(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={12} className="text-end mt-2">
                      <Button 
                        type="submit" 
                        variant="primary" 
                        disabled={!selectedMedicineId || assigningMed || unassignedMedicinesForSlot.length === 0}
                        className="fw-semibold px-4"
                      >
                        {assigningMed ? <Spinner size="sm" animation="border" /> : 'Assign to Slot'}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 d-flex justify-content-between align-items-center">
          <Button 
            variant="outline-danger" 
            size="sm"
            className="d-flex align-items-center gap-2 fw-semibold"
            onClick={() => {
              const slot = selectedSlot;
              setShowSlotModal(false);
              confirmDeleteSlot(slot);
            }}
          >
            <FaTrash /> Delete This Compartment Slot
          </Button>
          <Button variant="secondary" onClick={() => setShowSlotModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL 6: Delete Slot Confirmation Modal */}
      <Modal show={showDeleteSlotModal} onHide={() => setShowDeleteSlotModal(false)} centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="text-danger fw-bold d-flex align-items-center gap-2">
            <FaExclamationCircle /> Delete Compartment Slot
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete compartment slot <strong>{slotToDelete?.slot_code}</strong>?
          </p>
          <div className="modal-warning-callout">
            <FaExclamationCircle className="text-warning flex-shrink-0 mt-1" />
            <div>
              Any medicines currently assigned to this compartment will be unassigned. The remaining slots on this shelf will remain intact.
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="light" onClick={() => setShowDeleteSlotModal(false)} disabled={deletingSlot}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={executeDeleteSlot} 
            disabled={deletingSlot}
            className="fw-semibold d-flex align-items-center gap-2"
          >
            {deletingSlot ? <Spinner size="sm" animation="border" /> : <><FaTrash /> Delete Slot</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL 7: Delete Shelf Confirmation Modal */}
      <Modal show={showDeleteShelfModal} onHide={() => setShowDeleteShelfModal(false)} centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="text-danger fw-bold d-flex align-items-center gap-2">
            <FaExclamationCircle /> Delete Shelf Row
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete <strong>{shelfToDelete?.shelf_label || `Shelf ${shelfToDelete?.shelf_number}`}</strong>?
          </p>
          <div className="modal-warning-callout">
            <FaExclamationCircle className="text-warning flex-shrink-0 mt-1" />
            <div>
              This will permanently remove this shelf and all its compartment slots. Any medicines assigned to these slots will be unassigned.
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="light" onClick={() => setShowDeleteShelfModal(false)} disabled={deletingShelf}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={executeDeleteShelf} 
            disabled={deletingShelf}
            className="fw-semibold d-flex align-items-center gap-2"
          >
            {deletingShelf ? <Spinner size="sm" animation="border" /> : <><FaTrash /> Delete Shelf</>}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default RackManagement;
